import { ButtonAdd, Divider, Modal, State, Toast } from '@seiue/ui'
import React, { FC, useEffect } from 'react'
import styled from 'styled-components'

import { $t } from 'packages/locale'
import { notificationApi, StatusEnum } from 'packages/sdks-next/growp'

import { NoticeDetail } from '@/components/NoticeDetail'
import { NotificationItem } from '@/components/NotificationItem'
import { HotNewsDetails } from '@/features/hot-news/components/HotNewsDetails'
import { openEditHotNewsModal } from '@/features/hot-news/utils'

/**
 * 资讯列表
 *
 * @param param0 - props
 * @param param0.type - 类型
 * @param param0.hasHotNewsPermission - 是否有资讯权限
 * @param param0.selectedKey - 选中的 key
 * @returns FC
 */
export const HotNewsList: FC<{
  type: 'inbox' | 'outbox'
  hasHotNewsPermission?: boolean
  selectedKey?: string
}> = ({ type, hasHotNewsPermission, selectedKey }) => {
  const isInbox = type === 'inbox'
  const {
    data: news,
    loading,
    reload: reloadList,
  } = notificationApi.useQueryFrontendInformations({
    isReceived: isInbox,
    paginated: 0,
    sort: isInbox ? undefined : '-id',
    expand: isInbox
      ? [
          'currentRecever',
          ['currentRecever', 'isReaded'],
          'senderName',
          'setting',
        ]
      : [
          'receverRoles',
          'senderName',
          'readedCount',
          'receverRoles',
          'setting',
        ],
  })

  // 查询资讯的统计
  const { data: stat, reload: reloadStat } =
    notificationApi.useStatFrontendInformations({
      isReceived: isInbox,
    })

  const reload = () => {
    reloadList()
    reloadStat()
  }

  useEffect(() => {
    reload()
  }, [selectedKey])

  if (loading) return <State.Loading />

  return (
    <div>
      <Header>
        <Title>
          {`共 ${news?.length ?? 0} 条资讯`}
          {type === 'inbox' ? `，${stat?.unreadCount ?? 0} 条未读` : ''}
        </Title>
        {type === 'outbox' ? (
          <ButtonAdd
            type="primary"
            size="small"
            onClick={() => {
              openEditHotNewsModal({
                reload,
              })
            }}
          >
            {$t('新建资讯')}
          </ButtonAdd>
        ) : null}
      </Header>
      <Divider />
      <List $hasHotNewsPermission={hasHotNewsPermission}>
        {news?.map((news) => {
          const openNews = async () => {
            if (isInbox) {
              if ((news.setting as { isLink?: boolean } | undefined)?.isLink) {
                // 打开新的窗口
                window.open(news.content)
              } else {
                Modal.open({
                  title: '资讯详情',
                  children: <NoticeDetail notification={news} />,
                  footer: null,
                  width: 960,
                  height: 600,
                  bodyStyle: { padding: 20 },
                })
              }

              if (!news.currentRecever?.isReaded) {
                await notificationApi.readNotification(news.id)
                reload()
              }
            } else {
              Modal.open({
                title: '资讯详情',
                children: <HotNewsDetails notification={news} />,
                width: 960,
                height: 720,
                footer: null,
                bodyStyle: { padding: '0 20px' },
              })
            }
          }

          const items = [
            {
              label: $t('查看'),
              callback: async () => {
                await openNews()
              },
              visible: true,
            },
            {
              label: $t('编辑'),
              callback: () => {
                openEditHotNewsModal({
                  notification: news,
                  reload,
                  justEditContent: news.status === StatusEnum.Published,
                })
              },
              visible: news.status !== StatusEnum.Withdrawn,
            },
            {
              label: $t('重新发送'),
              callback: async () => {
                openEditHotNewsModal({
                  notification: news,
                  reload,
                })
              },
              visible: news.status === StatusEnum.Withdrawn,
            },
            {
              label: $t('撤回'),
              callback: () => {
                Modal.confirm({
                  title: '确认要撤回资讯吗？',
                  content: '撤回后发送对象将无法看到此条资讯，确定要撤回吗？',
                  onOk: async () => {
                    await notificationApi.modifyNotificationStatus(
                      StatusEnum.Withdrawn,
                      news.id,
                      {}
                    )
                    reload()
                    Toast.success('撤回成功')
                  },
                })
              },
              visible: news.status === StatusEnum.Published,
            },
            {
              label: $t('删除'),
              callback: () => {
                Modal.danger({
                  title: '确认要删除资讯吗？',
                  content: '删除后数据将无法恢复，确定删除吗？',
                  onOk: async () => {
                    await notificationApi.deleteNotification(news.id)
                    reload()
                    Toast.success('删除成功')
                  },
                })
              },
              visible: news.status !== StatusEnum.Published,
            },
          ].filter((item) => item.visible)

          return (
            <NotificationItem
              key={news.id}
              notification={news}
              actionItems={items}
              showSenderName={isInbox}
              readonly={isInbox}
              itemClick={openNews}
            />
          )
        })}
      </List>
    </div>
  )
}

const Header = styled.div`
  display: flex;
  padding: 16px 0;
  align-items: center;
  justify-content: space-between;
`

const Title = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  color: ${({ theme }) => theme.text._1};
`

const List = styled.div<{ $hasHotNewsPermission?: boolean }>`
  display: flex;
  flex-direction: column;
  height: ${({ $hasHotNewsPermission }) =>
    $hasHotNewsPermission ? 'calc(100vh - 242px)' : 'calc(100vh - 198px)'};
  overflow: auto;
  // 通过布局将滚动条向右偏移 20px
  padding-right: 24px;
  margin-right: -24px;
`
